export interface LabHasilItem {
  test_id?: string;
  nama_test?: string;
  hasil?: string;
  satuan?: string;
  nilai_normal?: string;
  flag?: string;
}

export interface LabOrderItem {
  no_order?: string;
  tgl_order?: string;
  lis_sampel?: string;
  acc_by?: string;
  acc_date?: string;
  hasil?: Array<LabHasilItem>;
}

export interface LabPraInduksiSumber {
  lis_sampel: string;
  acc_date: string;
  tgl_order: string;
}

export interface LabPraInduksiHasil {
  values: Record<string, number | string>;
  labels: Array<string>;
  sumber: LabPraInduksiSumber | null;
}

const LABEL: Record<string, string> = {
  hb: "HB",
  al: "AL",
  at: "AT",
  hmt: "HMT",
  pt: "PT",
  aptt: "APTT",
  gd: "GD",
  elektrolit: "Elektrolit",
  saturasi_o2: "Saturasi O2"
};

const URUTAN_FIELD = [
  "hb",
  "al",
  "at",
  "hmt",
  "pt",
  "aptt",
  "gd",
  "elektrolit",
  "saturasi_o2"
];

export function normalize(text?: string): string {
  return String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function isControl(nama: string): boolean {
  return /\b(control|kontrol|ctrl)\b/.test(nama);
}

function toNumber(hasil?: string): number | null {
  const raw = String(hasil == null ? "" : hasil)
    .trim()
    .replace(",", ".");

  if (!/^-?\d+(\.\d+)?$/.test(raw)) return null;

  const num = parseFloat(raw);

  return isNaN(num) ? null : num;
}

function isRibuan(satuan?: string): boolean {
  const s = String(satuan || "").toLowerCase();

  return /10\s*\^?\s*3|10³|ribu|\bk\s*\/\s*u?l\b/.test(s);
}

function matchField(nama: string): string | null {
  if (/hemoglobin|\bhgb\b|\bhb\b/.test(nama) && !/hba1c|\bhbsag\b/.test(nama)) {
    return "hb";
  }
  if (/leukosit|lekosit|\bwbc\b|\bal\b/.test(nama)) return "al";
  if (/trombosit|thrombosit|platelet|\bplt\b|\bat\b/.test(nama)) return "at";
  if (/hematokrit|hematocrit|haematokrit|\bhct\b|\bht\b/.test(nama)) {
    return "hmt";
  }
  if (/\bppt\b|\bpt\b|protrombin|prothrombin/.test(nama) && !isControl(nama)) {
    return "pt";
  }
  if (/\baptt\b|thromboplastin/.test(nama) && !isControl(nama)) return "aptt";
  if (/glukosa|gula darah|\bgds\b|\bgdp\b/.test(nama) && !/hba1c/.test(nama)) {
    return "gd";
  }
  if (/saturasi|\bso2\b|\bsao2\b|\bspo2\b/.test(nama)) return "saturasi_o2";

  return null;
}

function matchElektrolit(nama: string): string | null {
  if (/natrium|sodium|\bna\b/.test(nama)) return "Na";
  if (/kalium|potassium|\bk\b/.test(nama)) return "K";
  if (/klorida|chlorida|chloride|\bcl\b/.test(nama)) return "Cl";

  return null;
}

export function waktuOrder(order: LabOrderItem): number {
  const waktu = order.acc_date || order.tgl_order || "";
  const stamp = new Date(String(waktu).replace(" ", "T")).getTime();

  return isNaN(stamp) ? 0 : stamp;
}

function ambilDariOrder(order: LabOrderItem): Record<string, number | string> {
  const hasil = Array.isArray(order.hasil) ? order.hasil : [];
  const found: Record<string, number | string> = {};
  const elektrolit: Array<string> = [];

  hasil.forEach(item => {
    const nama = normalize(item.nama_test);
    if (!nama) return;

    const ion = matchElektrolit(nama);
    if (ion) {
      const nilai = String(item.hasil || "").trim();
      if (nilai) {
        elektrolit.push(
          `${ion} ${nilai}${item.satuan ? " " + String(item.satuan).trim() : ""}`
        );
      }
      return;
    }

    const field = matchField(nama);
    if (!field) return;

    const angka = toNumber(item.hasil);
    if (angka === null) return;

    if (field === "saturasi_o2" && (angka < 0 || angka > 100)) return;

    let nilai = angka;
    if ((field === "al" || field === "at") && isRibuan(item.satuan)) {
      nilai = Math.round(angka * 1000);
    }

    if (field === "gd") {
      const sewaktu = /sewaktu|\bgds\b/.test(nama);
      if (found.gd !== undefined && !sewaktu) return;
    } else if (found[field] !== undefined) {
      return;
    }

    found[field] = nilai;
  });

  if (elektrolit.length) {
    found.elektrolit = elektrolit.join(", ").slice(0, 500);
  }

  return found;
}

export function mapHasilLabPraInduksi(
  orders: Array<LabOrderItem>
): LabPraInduksiHasil {
  const values: Record<string, number | string> = {};
  let sumber: LabPraInduksiSumber | null = null;

  const terbaruDulu = (orders || [])
    .slice()
    .sort((a, b) => waktuOrder(b) - waktuOrder(a));

  terbaruDulu.forEach(order => {
    const found = ambilDariOrder(order);
    let dipakai = false;

    Object.keys(found).forEach(field => {
      if (values[field] !== undefined) return;
      values[field] = found[field];
      dipakai = true;
    });

    if (dipakai && !sumber) {
      sumber = {
        lis_sampel: String(order.lis_sampel || ""),
        acc_date: String(order.acc_date || ""),
        tgl_order: String(order.tgl_order || "")
      };
    }
  });

  const labels = URUTAN_FIELD.filter(
    field => values[field] !== undefined
  ).map(field => LABEL[field]);

  return { values, labels, sumber };
}

export default { mapHasilLabPraInduksi };
